import React, { useEffect, useState } from "react";
import {
  Card,
  Badge,
  LoadingState,
  EmptyState,
  ErrorState,
} from "@/components/design-system";
import { adminReferralLedger } from "@/lib/referrals";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");
const TABS = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "converted", label: "Verified" },
  { id: "expired", label: "Expired" },
];

const STATUS_BADGE = {
  pending: { variant: "neutral", label: "Pending" },
  converted: { variant: "active", label: "Verified" },
  expired: { variant: "admin", label: "Expired" },
};

function Stat({ label, value, testId }) {
  return (
    <Card className="!p-4">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">
        {label}
      </div>
      <div
        data-testid={testId}
        className="mt-1 font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A]"
      >
        {value}
      </div>
    </Card>
  );
}

export default function AdminReferrals() {
  const [tab, setTab] = useState("");
  const [state, setState] = useState({ status: "loading", data: null, err: "" });

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const data = await adminReferralLedger(tab || undefined);
      setState({ status: "ready", data, err: "" });
    } catch (err) {
      setState({ status: "error", data: null, err: formatApiError(err) });
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div data-testid="admin-referrals-page" className="space-y-6">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">
          Administrator · Referral ledger
        </p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Referral attributions
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Every signup attributed to a Verified Ambassador with a 12-month commission
          window. Payout automation lands in Cycle P3.
        </p>
      </div>

      {state.data?.counters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Active codes" value={state.data.counters.codes} testId="ledger-stat-codes" />
          <Stat label="Clicks" value={state.data.counters.clicks} testId="ledger-stat-clicks" />
          <Stat label="Pending" value={state.data.counters.pending} testId="ledger-stat-pending" />
          <Stat label="Verified" value={state.data.counters.converted} testId="ledger-stat-converted" />
          <Stat label="Expired" value={state.data.counters.expired} testId="ledger-stat-expired" />
        </div>
      )}

      <div className="inline-flex items-center gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-white">
        {TABS.map((t) => (
          <button
            key={t.id || "all"}
            data-testid={`ledger-tab-${t.id || "all"}`}
            onClick={() => setTab(t.id)}
            className={cn(
              "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider",
              tab === t.id ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        {state.status === "loading" && <LoadingState label="Loading ledger…" />}
        {state.status === "error" && <ErrorState detail={state.err} />}
        {state.status === "ready" && state.data.items.length === 0 && (
          <EmptyState
            title="No attributions yet"
            description={
              tab
                ? `Nothing in "${tab}" bucket.`
                : "As soon as an ambassador brings in a signup, it will appear here."
            }
          />
        )}
        {state.status === "ready" && state.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-referral-ledger-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 border-b border-[#0B1F3A]/10">
                  <th className="py-2 pr-3">Ambassador</th>
                  <th className="py-2 pr-3">Referred user</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Signed up</th>
                  <th className="py-2 pr-3">Verified at</th>
                  <th className="py-2 pr-3">Window ends</th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((a) => {
                  const meta = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-[#0B1F3A]/5 align-top"
                      data-testid={`ledger-row-${a.id}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-[#0B1F3A]">
                          {a.ambassador?.name || "—"}
                        </div>
                        <div className="text-xs text-[#0B1F3A]/60">
                          {a.ambassador?.email}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-[#0B1F3A]">
                          {a.referred?.name || "—"}
                        </div>
                        <div className="text-xs text-[#0B1F3A]/60">
                          {a.referred?.email}
                        </div>
                      </td>
                      <td className="py-2 pr-3 font-mono text-[13px] text-[#0B1F3A]">
                        {a.code}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-[#0B1F3A]/85">
                        {a.signup_at ? new Date(a.signup_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3 text-[#0B1F3A]/85">
                        {a.converted_at
                          ? new Date(a.converted_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="py-2 pr-3 text-[#0B1F3A]/60">
                        {a.expires_at
                          ? new Date(a.expires_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
